import { useState, useCallback, useMemo, useEffect } from "react";
import axiosInstance from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/api/apiConfig";
import { useQuery } from "@tanstack/react-query";

export interface Admin {
    id: number;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    address: string;
    phone: string;
    email: string;
}

interface AdminFilters {
    searchTerm: string;
    currentPage: number;
    adminsPerPage: number;
}

const DEFAULT_FILTERS: AdminFilters = {
    searchTerm: "",
    currentPage: 1,
    adminsPerPage: 10,
};

export const useAdminAccountList = () => {
    const [filters, setFilters] = useState<AdminFilters>(DEFAULT_FILTERS);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(filters.searchTerm);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(filters.searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [filters.searchTerm]);

    const { data, isLoading: loading, error: queryError, refetch } = useQuery({
        queryKey: ['admin', 'accounts', { ...filters, searchTerm: debouncedSearchTerm }],
        queryFn: async () => {
            const response = await axiosInstance.get(API_ENDPOINTS.USERS.SEARCH, {
                params: {
                    role: "ADMIN",
                    query: debouncedSearchTerm,
                    page: filters.currentPage - 1,
                    size: filters.adminsPerPage,
                },
            });
            return response.data;
        },
    });

    const updateFilters = useCallback((newFilters: Partial<AdminFilters>) => {
        setFilters(prev => ({
            ...prev,
            ...newFilters,
            currentPage: newFilters.searchTerm !== undefined ? 1 : (newFilters.currentPage ?? prev.currentPage)
        }));
    }, []);

    const admins = useMemo(() => data?.content || [], [data]);
    const totalPages = data?.totalPages || 1;

    const error = queryError ? (queryError as any).message : null;

    return {
        admins,
        loading,
        error,
        totalPages,
        filters,
        setFilters: updateFilters,
        refetch
    };
};
